import React, { useState, useEffect } from 'react';

interface GeminiApiKey {
  id: number;
  api_key: string;
  email: string;
  user_id: number;
  created_at: string;
  updated_at: string;
}

const GeminiApiKeyManagement: React.FC = () => {
  const [apiKey, setApiKey] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [apiKeys, setApiKeys] = useState<GeminiApiKey[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/gemini_api_keys');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: GeminiApiKey[] = await response.json();
      setApiKeys(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      const response = await fetch('/api/gemini_api_keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content,
        },
        body: JSON.stringify({ gemini_api_key: { api_key: apiKey, email } }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.api_key || errorData.email || 'Failed to save API key');
      }

      setApiKey('');
      setEmail('');
      fetchApiKeys(); // Refresh the list
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Gemini API Key Management</h1>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8">
        <div className="mb-4">
          <label htmlFor="apiKey" className="block text-gray-700 text-sm font-bold mb-2">
            API Key:
          </label>
          <input
            type="text"
            id="apiKey"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
            Associated Email:
          </label>
          <input
            type="email"
            id="email"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Add API Key
        </button>
      </form>

      <h2 className="text-xl font-bold mb-4">Your Saved API Keys</h2>
      {loading ? (
        <p>Loading API keys...</p>
      ) : apiKeys.length === 0 ? (
        <p>No API keys saved yet.</p>
      ) : (
        <ul className="bg-white p-6 rounded-lg shadow-md">
          {apiKeys.map((key) => (
            <li key={key.id} className="border-b border-gray-200 py-2 last:border-b-0">
              <p className="text-gray-800">
                <strong>API Key:</strong> {key.api_key.substring(0, 5)}... (hidden for security)
              </p>
              <p className="text-gray-600">
                <strong>Email:</strong> {key.email}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GeminiApiKeyManagement;